import { autoinject, bindable, bindingMode } from "aurelia-framework";
import {
  validateTrigger,
  ValidationController,
  ValidationControllerFactory,
  ValidationRules,
} from "aurelia-validation";
import { BootstrapFormRenderer } from "../../components/bootstrap-form-renderer";
import { LoginStage } from "../../enums/login-stage";
import { appLogger } from "../../infrastructure/logging";
import {
  ChallengeService,
  IPasswordStatusResult,
} from "../../services/challenge-service";
import { ProfileService } from "../../services/profile-service";
import { CountdownTimer } from "../../utilities/countdown-timer";

@autoinject
export class SecondFactor {
  @bindable({ defaultBindingMode: bindingMode.twoWay })
  public finalize: () => Promise<void>;
  @bindable({ defaultBindingMode: bindingMode.twoWay })
  public stage: LoginStage;
  public subStage: string;
  public isText: boolean = null;
  public secondFactorCode?: string;
  private _challengeService: ChallengeService;
  private readonly _profileService: ProfileService;
  public secondFactorController: ValidationController;
  public countdownTimer : CountdownTimer; 
  public smsTimeLimit: number = 180;
  public callTimeLimit: number = 600;
  public hasError: boolean = false;
  public isLoading: boolean = false;
  public twoFactorFocused: boolean = false;
  public challengeType: number;
  public maskedMobileNumber: string = "";
  public isEmailUnderToggle: boolean = false;
  @bindable({ defaultBindingMode: bindingMode.twoWay })
  public isTwoFAActive: boolean = false;
  @bindable({ defaultBindingMode: bindingMode.twoWay })
  public passwordCheckResponse: IPasswordStatusResult;

  public constructor(
    challengeService: ChallengeService,
    profileService: ProfileService,
    controllerFactory: ValidationControllerFactory
  ) {
    this._challengeService = challengeService;
    this._profileService = profileService;
    this.secondFactorController = controllerFactory.create();
    this.secondFactorController.validateTrigger = validateTrigger.changeOrBlur;
    this.secondFactorController.addObject(this, secondFactorRules);
    this.secondFactorController.addRenderer(new BootstrapFormRenderer());
  }

  public attached(){
        this.countdownTimer = new CountdownTimer();
        this.restoreSecondFactorState();
  }

  public saveSecondFactorState() {
    const state = {
      subStage: this.subStage,
      isText: this.isText,
      challengeType: this.challengeType,
      maskedMobileNumber: this.maskedMobileNumber,
      isTwoFAActive: this.isTwoFAActive,
      isEmailUnderToggle: this.isEmailUnderToggle,
    };
    window.sessionStorage.setItem("secondFactorState", JSON.stringify(state));
    window.sessionStorage.setItem(
      "secondFactorReturnUrl",
      window.location.pathname + window.location.search
    );
    window.location.assign("/disable-two-factor-authentication");
  }

  private restoreSecondFactorState() {
    if (window.sessionStorage.getItem("restoreSecondFactor") !== "true") {
      return;
    }
    const raw = window.sessionStorage.getItem("secondFactorState");
    if (raw) {
      const state = JSON.parse(raw);
      this.subStage = state.subStage;
      this.isText = state.isText;
      this.challengeType = state.challengeType;
      this.maskedMobileNumber = state.maskedMobileNumber;
      this.isTwoFAActive = state.isTwoFAActive;
      this.isEmailUnderToggle = state.isEmailUnderToggle;
      this.stage = LoginStage.secondFactor;
      if (this.subStage === "otp-type-selected") {
        this.countdownTimer.setTimer(state.isText ? this.smsTimeLimit : this.callTimeLimit);
      }
    }
    window.sessionStorage.removeItem("restoreSecondFactor");
    window.sessionStorage.removeItem("secondFactorState");
    window.sessionStorage.removeItem("secondFactorReturnUrl");
  }

  public async challengeSecondFactor() {
    let secondFactorSuccess = await this.submitSecondFactor();
    if (!secondFactorSuccess) {
      //should change logic as its workaround ,and now you will recieve code for 2fa type 0 for no
      if (this.secondFactorCode != null) {
        this.hasError = true;
      }
      return;
    }
    await this.finalize();
  }

  public async callOtpApi() {
    if(this.countdownTimer.isResend || this.subStage === "get-otp-type"){
    this.countdownTimer.setTimer(this.isText ? this.smsTimeLimit : this.callTimeLimit);
     await this._challengeService.selectOtpCodePreference(
      this.isText ? "text" : "call"
    );
    if(this.subStage === "get-otp-type"){
      this.countdownTimer.isResend = true;
    }
    this.hasError = false;
    this.subStage = "otp-type-selected";
  }
  }

  public async submitSecondFactor() {
    this.isEmailUnderToggle =
      window.sessionStorage.getItem("isEmailUnderToggle") === "true";
    this.isLoading = true;
    try {
      // remove if block After 2FA testing
      if (!this.isEmailUnderToggle) {
        let result = await this._challengeService.challengeSecondFactor(
          this.secondFactorCode
        );
        this.isLoading = false;
        let failure = result == null;
        if (failure) {
          this.stage = LoginStage.secondFactor;
          this.secondFactorCode = null;
          this.twoFactorFocused = true;
          appLogger.warn("Code check failed.");
        } else {
          this.passwordCheckResponse =
            await this._challengeService.passwordCheckChallenge();
          if (this.passwordCheckResponse.isPasswordChangeRequired) {
            this.stage = LoginStage.updatePassword;
          }
        }
        return !failure;
      } else {
        const totpResp = await this._challengeService.getTotpStatus();
        this.isTwoFAActive = totpResp?.active;
        let isSuccess = true;
        if (this.isTwoFAActive) {
          if (this.stage !== LoginStage.secondFactor) {
            this.challengeType = totpResp?.challengeType;
            this.stage = LoginStage.secondFactor;
            if (this.challengeType == 2){
                this.maskedMobileNumber= `###-###-${totpResp.mobileNumber?.slice(-4)}`
                 this.subStage = "get-otp-type";
            }
          }

          let result = null;
          if (this.secondFactorCode) {
            if (this.challengeType == 0) {
              result = await this._challengeService.challengeSecondFactor(
                this.secondFactorCode
              );
            } else {
              result = await this._challengeService.verifyTextCallOtpChallange(
                this.secondFactorCode
              );
            }
          }
          isSuccess = result !== null;
          
          if (!isSuccess) {
              this.secondFactorCode = null;
              this.twoFactorFocused = true;
              appLogger.warn("Code check failed.");
            }
        }
        if (!this.isTwoFAActive) {
          await this._challengeService.challengeSecondFactor(
            this.secondFactorCode
          );
        }
        if (isSuccess) {
            this.passwordCheckResponse =
            await this._challengeService.passwordCheckChallenge();
            if (this.passwordCheckResponse.isPasswordChangeRequired) {
                this.stage = LoginStage.updatePassword;
            }
        }
        return isSuccess;
      }
    } catch (e) {
      if (this.secondFactorCode != null) {
        this.hasError = true;
      }
      return false;
    }finally{
        this.isLoading = false;
    }
  }

  public clearSecondFactorCode() {
    this.secondFactorCode = null;
  }

  public async challengeSecurityKey() {
    const response =
      (await this._challengeService.getSecurityLoginOptions()) as any;
    response.options.challenge = this.base64ToArrayBuffer(
      response.options.challenge
    );
    response.options.allowCredentials = response.options.allowCredentials.map(
      (cred: any) => {
        return {
          type: "public-key",
          id: this.base64ToArrayBuffer(cred.id),
        };
      }
    );
    let result = (await navigator.credentials.get({
      publicKey: response.options,
    })) as any;
    const payload = {
      ...result,
      rawId: this.bufferToBase64URL(result.rawId),
      id: result.id,
      type: result.type,
      response: {
        clientDataJSON: this.bufferToBase64URL(result.response.clientDataJSON),
        authenticatorData: this.bufferToBase64URL(
          result.response.authenticatorData
        ),
        signature: this.bufferToBase64URL(result.response.signature),
        userHandle: response.userHandle
          ? this.bufferToBase64URL(result.response.userHandle)
          : null,
      },
    };
    const res = (await this._challengeService.verifySecurityKey(
      payload
    )) as any;
    this.passwordCheckResponse =
      await this._challengeService.passwordCheckChallenge();
    await this.finalize();
  }

  public base64ToArrayBuffer(base64: any) {
    const binary = atob(base64.replace(/-/g, "+").replace(/_/g, "/"));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes.buffer;
  }

  public bufferToBase64URL(buffer: any) {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  }

  public detached(){
    this.countdownTimer.stop();
  }
}

const secondFactorRules = ValidationRules.ensure(
  (x: SecondFactor) => x.secondFactorCode
)
  .required()
  .withMessage("Code is required.")
  .matches(/^\d{6}$/)
  .withMessage("Code must be 6 digits.").rules;
